import { useState } from 'react'
import { X, MapPin, Clock, CheckCircle, AlertCircle, ThumbsUp, Edit2, Trash2 } from 'lucide-react'
import { Comment } from '../../types/comments'
import { getCommentMarkerIcon, getCommentMarkerColor } from './CommentMarker'

interface CommentDetailModalProps {
  comment: Comment | null
  isOpen: boolean
  onClose: () => void
  onConfirm?: (commentId: string) => void
  onDispute?: (commentId: string) => void
  onHelpful?: (commentId: string, isHelpful: boolean) => void
  onEdit?: (commentId: string, text: string) => void
  onDelete?: (commentId: string) => void
  onResolve?: (commentId: string, resolved: boolean) => void
  currentUserId?: string
  isAuthor?: boolean
}

export function CommentDetailModal({
  comment,
  isOpen,
  onClose,
  onConfirm,
  onDispute,
  onHelpful,
  onEdit,
  onDelete,
  onResolve,
  currentUserId,
  isAuthor = false
}: CommentDetailModalProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [editText, setEditText] = useState('')

  if (!isOpen || !comment) return null

  const Icon = getCommentMarkerIcon(comment.category)
  const color = getCommentMarkerColor(comment.category)
  
  const confirmCount = comment.confirmations?.length || 0
  const disputeCount = comment.disputes?.length || 0
  const helpfulCount = comment.helpful?.length || 0
  
  const hasConfirmed = currentUserId && comment.confirmations?.includes(currentUserId)
  const hasDisputed = currentUserId && comment.disputes?.includes(currentUserId)
  const hasMarkedHelpful = currentUserId && comment.helpful?.includes(currentUserId)

  const timeSinceCreated = comment.createdAt 
    ? new Date().getTime() - comment.createdAt.toDate().getTime()
    : 0
  const isRecent = timeSinceCreated < 24 * 60 * 60 * 1000

  const handleEdit = () => {
    setEditText(comment.text)
    setIsEditing(true)
  }

  const handleSaveEdit = () => {
    if (editText.trim() && onEdit) {
      onEdit(comment.id, editText.trim())
      setIsEditing(false)
    }
  }

  const formatDate = (date: any) => {
    if (!date) return ''
    const d = date.toDate ? date.toDate() : date
    return d.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    })
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center" style={{ fontSize: 'min(1rem, 1.5vw)', paddingBottom: '20vh' }}>
      <div className="bg-gray-800 rounded-xl shadow-2xl flex flex-col border border-gray-700/50 overflow-hidden" style={{ width: '90vw', maxWidth: '500px', maxHeight: '75vh' }}>
        {/* Header */}
        <div className="border-b border-gray-700 bg-gray-800" style={{ padding: '0.75em' }}>
          <div className="flex items-start justify-between">
            <div className="flex items-start" style={{ gap: '0.5em' }}>
              <div 
                className="rounded-full flex items-center justify-center"
                style={{ backgroundColor: color + '33', width: '2em', height: '2em' }}
              >
                <Icon style={{ color, width: '1em', height: '1em' }} />
              </div>
              <div>
                <h3 className="font-medium text-white flex items-center" style={{ gap: '0.5em', fontSize: '1em' }}>
                  {comment.category.charAt(0).toUpperCase() + comment.category.slice(1)}
                  {isRecent && (
                    <span className="bg-emerald-600 text-white rounded" style={{ fontSize: '0.75em', padding: '0.125em 0.5em' }}>
                      New
                    </span>
                  )}
                  {comment.resolved && (
                    <span className="bg-gray-600 text-gray-300 rounded" style={{ fontSize: '0.75em', padding: '0.125em 0.5em' }}>
                      Resolved
                    </span>
                  )}
                </h3>
                <div className="flex items-center text-gray-400" style={{ gap: '0.75em', fontSize: '0.75em', marginTop: '0.25em' }}>
                  {comment.type === 'pinned' && comment.distanceAlongRoute !== undefined && (
                    <span className="flex items-center" style={{ gap: '0.25em' }}>
                      <MapPin style={{ width: '0.75em', height: '0.75em' }} />
                      {comment.distanceAlongRoute.toFixed(1)} km
                    </span>
                  )}
                  <span className="flex items-center" style={{ gap: '0.25em' }}>
                    <Clock style={{ width: '0.75em', height: '0.75em' }} />
                    {formatDate(comment.createdAt)}
                  </span>
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="hover:bg-gray-700 rounded-lg transition-colors"
              style={{ padding: '0.25em' }}
            >
              <X className="text-gray-400" style={{ width: '1.25em', height: '1.25em' }} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto" style={{ padding: '1em' }}>
          {/* Author */}
          <div className="flex items-center" style={{ gap: '0.5em', marginBottom: '0.75em' }}>
            {comment.authorPhoto ? (
              <img 
                src={comment.authorPhoto} 
                alt={comment.authorName}
                className="rounded-full"
                style={{ width: '2em', height: '2em' }}
              />
            ) : (
              <div className="rounded-full bg-gray-700 flex items-center justify-center" style={{ width: '2em', height: '2em' }}>
                <span className="font-medium text-gray-300" style={{ fontSize: '0.75em' }}>
                  {comment.authorName.charAt(0).toUpperCase()}
                </span>
              </div>
            )}
            <span className="text-gray-300" style={{ fontSize: '0.875em' }}>{comment.authorName}</span>
          </div>

          {/* Comment Text */}
          {isEditing ? (
            <div className="space-y-2">
              <textarea
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500"
                rows={4}
                autoFocus
              />
              <div className="flex gap-2">
                <button
                  onClick={handleSaveEdit}
                  className="px-3 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md text-sm transition-colors"
                >
                  Save
                </button>
                <button
                  onClick={() => setIsEditing(false)}
                  className="px-3 py-1 bg-gray-700 hover:bg-gray-600 text-white rounded-md text-sm transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <p className="text-gray-200 whitespace-pre-wrap">{comment.text}</p>
          )}

          {/* Validity Period */}
          {comment.validUntil && (
            <div className="mt-3 p-2 bg-gray-700/50 rounded-lg">
              <p className="text-xs text-gray-400">
                Valid until: {formatDate(comment.validUntil)}
              </p>
            </div>
          )}

          {/* Engagement Stats */}
          <div className="mt-4 flex items-center gap-4">
            {confirmCount > 0 && (
              <div className="flex items-center gap-1 text-green-400 text-sm">
                <CheckCircle className="w-4 h-4" />
                <span>{confirmCount}</span>
              </div>
            )}
            {disputeCount > 0 && (
              <div className="flex items-center gap-1 text-red-400 text-sm">
                <AlertCircle className="w-4 h-4" />
                <span>{disputeCount}</span>
              </div>
            )}
            {helpfulCount > 0 && (
              <div className="flex items-center gap-1 text-blue-400 text-sm">
                <ThumbsUp className="w-4 h-4" />
                <span>{helpfulCount}</span>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="border-t border-gray-700" style={{ padding: '1em' }}>
          {/* Engagement Actions */}
          {currentUserId && !isAuthor && (
            <div className="flex gap-2">
              {(comment.category === 'hazard' || comment.category === 'condition') && (
                <>
                  <button
                    onClick={() => onConfirm?.(comment.id)}
                    className={`
                      flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-sm transition-colors
                      ${hasConfirmed 
                        ? 'bg-green-600 text-white hover:bg-green-700' 
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      }
                    `}
                  >
                    <CheckCircle className="w-4 h-4" />
                    {hasConfirmed ? 'Confirmed' : 'Confirm'}
                  </button>
                  <button
                    onClick={() => onDispute?.(comment.id)}
                    className={`
                      flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-sm transition-colors
                      ${hasDisputed 
                        ? 'bg-red-600 text-white hover:bg-red-700' 
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      }
                    `}
                  >
                    <AlertCircle className="w-4 h-4" />
                    {hasDisputed ? 'Disputed' : 'Dispute'}
                  </button>
                </>
              )}
              <button
                onClick={() => onHelpful?.(comment.id, !hasMarkedHelpful)}
                className={`
                  flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-sm transition-colors
                  ${hasMarkedHelpful 
                    ? 'bg-blue-600 text-white hover:bg-blue-700' 
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }
                `}
              >
                <ThumbsUp className="w-4 h-4" />
                {hasMarkedHelpful ? 'Helpful' : 'Mark Helpful'}
              </button>
            </div>
          )}

          {/* Author Actions */}
          {isAuthor && (
            <div className="flex gap-2">
              {!isEditing && (
                <>
                  <button
                    onClick={handleEdit}
                    className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg text-sm transition-colors"
                  >
                    <Edit2 className="w-4 h-4" />
                    Edit
                  </button>
                  {(comment.category === 'hazard' || comment.category === 'condition') && !comment.resolved && (
                    <button
                      onClick={() => onResolve?.(comment.id, true)}
                      className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm transition-colors"
                    >
                      <CheckCircle className="w-4 h-4" />
                      Mark Resolved
                    </button>
                  )}
                  <button
                    onClick={() => {
                      if (confirm('Are you sure you want to delete this comment?')) {
                        onDelete?.(comment.id)
                      }
                    }}
                    className="px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}